"use client";

import { useCallback, useState } from "react";
import { LuSearch } from "react-icons/lu";

import { Command, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { useUserSearch } from "@/hooks/use-user-search";
import type { PartialAccount } from "@/utils/types";

import { Spinner } from "../spinner";
import { UserResult } from "./user-result";

interface GlobalSearchProps {
  placeholder?: string;
  baseRedirectUrl?: string;
  disabled?: boolean;
  users?: PartialAccount[];
  onSelect?: (account: PartialAccount) => void;
}

const UserMultipleSearch = ({
  placeholder = "Search any user...",
  baseRedirectUrl = "/",
  users = [],
  onSelect,
}: GlobalSearchProps) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const { loading: loadingSearchResults, searchResults } =
    useUserSearch(searchValue);

  const resetSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  const filteredSearchResults = searchResults.accounts.filter(
    (account) =>
      !users.find(
        (selectedUser) => selectedUser.publicKey === account.publicKey,
      ),
  );

  return (
    <form className="relative flex w-full items-center">
      <Command shouldFilter={false} className="rounded-full text-left">
        <Input
          placeholder={placeholder}
          className="pr-10"
          value={searchValue}
          onInput={(event) => {
            setSearchValue(event.currentTarget.value);
          }}
        />

        <CommandList
          className="
            absolute top-[40px] z-100 mt-1 max-h-[300px] w-full
            overflow-x-hidden overflow-y-auto rounded-xl bg-input
          "
        >
          {loadingSearchResults && (
            <Command className="rounded-xl border">
              <Spinner size={32} className="mx-auto my-2" />
            </Command>
          )}

          {filteredSearchResults.length > 0 && (
            <div className="rounded-xl border">
              {filteredSearchResults.map((account) => {
                return (
                  <UserResult
                    key={account.publicKey}
                    {...{
                      onSelect,
                      baseRedirectUrl,
                      account,
                      resetSearch,
                    }}
                  />
                );
              })}
            </div>
          )}
        </CommandList>
      </Command>

      <LuSearch
        className="
          pointer-events-none absolute top-[11px] right-4 size-4 cursor-pointer
          text-muted
        "
      />
    </form>
  );
};

export default UserMultipleSearch;
